import { useState, type CSSProperties, type ReactNode } from "react";
import { notoAnimSrc } from "@/lib/chatNotoEmoji";

function BurstGlyph({ emoji }: { emoji: string }): ReactNode {
  const src = notoAnimSrc(emoji);
  if (!src) return emoji;
  return <img className="chat-emoji-burst__anim" src={src} alt="" draggable={false} />;
}

let showerSeq = 0;

type ShowerParticle = {
  id: number;
  emoji: string;
  left: string;
  top: string;
  delay: string;
  duration: string;
  scale: number;
  rotate: number;
};

export type ChatEmojiShower = {
  id: number;
  particles: ShowerParticle[];
};

export type ChatEmojiLocalBurst = {
  id: number;
  emoji: string;
  x: number;
  y: number;
  particles: {
    id: number;
    dx: number;
    dy: number;
    delay: string;
    duration: string;
    scale: number;
    rotate: number;
  }[];
};

function buildShowerParticles(emoji: string, count = 32): ShowerParticle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    emoji,
    left: `${3 + Math.random() * 94}%`,
    top: `${4 + Math.random() * 92}%`,
    delay: `${Math.random() * 0.4}s`,
    duration: `${0.9 + Math.random() * 0.8}s`,
    scale: 0.7 + Math.random() * 1.15,
    rotate: -35 + Math.random() * 70,
  }));
}

function buildLocalParticles(count: number, hero = true) {
  const particles = Array.from({ length: count }, (_, i) => {
    const spread = -Math.PI * 0.08 + (i / Math.max(1, count - 1)) * Math.PI * 1.16;
    const jitter = (Math.random() - 0.5) * 0.42;
    const angle = spread + jitter;
    const dist = 46 + Math.random() * 72;
    return {
      id: i,
      dx: Math.cos(angle) * dist,
      dy: -Math.abs(Math.sin(angle)) * dist - 10 - Math.random() * 18,
      delay: `${Math.random() * 0.1}s`,
      duration: `${0.72 + Math.random() * 0.38}s`,
      scale: 0.85 + Math.random() * 0.7,
      rotate: -28 + Math.random() * 56,
    };
  });
  if (hero && particles[0]) {
    particles[0] = {
      ...particles[0],
      dx: (Math.random() - 0.5) * 14,
      dy: -16 - Math.random() * 10,
      delay: "0s",
      duration: "0.95s",
      scale: 2.4,
      rotate: -6 + Math.random() * 12,
    };
  }
  return particles;
}

function bubbleAnchor(messageId: number): { x: number; y: number } | null {
  const row = document.querySelector(`[data-chat-msg-id="${messageId}"]`);
  if (!(row instanceof HTMLElement)) return null;
  const bubble = row.querySelector(".chat-msg-bubble");
  const el = bubble instanceof HTMLElement ? bubble : row;
  const r = el.getBoundingClientRect();
  if (r.width < 2 && r.height < 2) return null;
  return { x: r.left + r.width / 2, y: r.top + r.height * 0.42 };
}

function reduceMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Burst local autour de la bulle ; un emoji plus gros dans le lot. */
export function useEmojiBursts() {
  const [shower, setShower] = useState<ChatEmojiShower | null>(null);
  const [local, setLocal] = useState<ChatEmojiLocalBurst | null>(null);

  function spawnBurst(emoji: string) {
    if (!emoji || reduceMotion()) return;
    const id = ++showerSeq;
    setShower({ id, particles: buildShowerParticles(emoji) });
    window.setTimeout(() => {
      setShower((current) => (current?.id === id ? null : current));
    }, 2000);
  }

  function spawnLocalBurst(emoji: string, messageId: number) {
    if (!emoji || reduceMotion()) return;
    const anchor = bubbleAnchor(messageId);
    if (!anchor) {
      spawnBurst(emoji);
      return;
    }
    const id = ++showerSeq;
    setLocal({
      id,
      emoji,
      x: anchor.x,
      y: anchor.y,
      particles: buildLocalParticles(12, true),
    });
    window.setTimeout(() => {
      setLocal((current) => (current?.id === id ? null : current));
    }, 1500);
  }

  return { shower, local, spawnBurst, spawnLocalBurst };
}

export function ChatEmojiBurstLayer({
  shower,
  local,
}: {
  shower: ChatEmojiShower | null;
  local?: ChatEmojiLocalBurst | null;
}) {
  return (
    <>
      {shower?.particles?.length ? (
        <div className="chat-emoji-shower" aria-hidden>
          {shower.particles.map((p) => (
            <span
              key={`${shower.id}-${p.id}`}
              className="chat-emoji-shower__particle"
              style={
                {
                  left: p.left,
                  top: p.top,
                  animationDelay: p.delay,
                  animationDuration: p.duration,
                  ["--emoji-scale"]: p.scale,
                  ["--emoji-rot"]: `${p.rotate}deg`,
                } as CSSProperties
              }
            >
              <BurstGlyph emoji={p.emoji} />
            </span>
          ))}
        </div>
      ) : null}
      {local?.particles?.length ? (
        <div className="chat-emoji-local" aria-hidden>
          {local.particles.map((p) => (
            <span
              key={`${local.id}-${p.id}`}
              className="chat-emoji-local__particle"
              style={
                {
                  left: local.x,
                  top: local.y,
                  animationDelay: p.delay,
                  animationDuration: p.duration,
                  ["--emoji-scale"]: p.scale,
                  ["--emoji-rot"]: `${p.rotate}deg`,
                  ["--dx"]: `${p.dx}px`,
                  ["--dy"]: `${p.dy}px`,
                } as CSSProperties
              }
            >
              <BurstGlyph emoji={local.emoji} />
            </span>
          ))}
        </div>
      ) : null}
    </>
  );
}
